import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { t } from '@reos/i18n';
import { EmptyState } from '@redhand/ui';
import { listContracts, type ContractDto } from '../lib/contracts-api.js';
import { ListTable } from '../components/list-table.js';
import { CONTRACT_STATUS_LABEL_KEY } from '../lib/contract-standing.js';

/**
 * `/elektronicke-podpisy` — Elektronické podpisy (krok T5, bod 3 auditu
 * klikacího mockupu).
 *
 * Do T4 byla položka v sekci „Připravujeme" (rozhodnutí R.B. z ADM1 bodu 7),
 * protože za ní nic nebylo. R.B. 29. 9. 2026 zadal, že sekce DOKUMENTY má mít
 * čtyři položky podle mockupu — takže tu je obrazovka STAVU podpisů nad
 * existujícími daty: které smlouvy čekají, na kom stojí a kam se kliká dál.
 *
 * Samotné podepsání (prostý a zaručený podpis) dodá krok ES1; tahle obrazovka
 * si nic nevymýšlí a nic nepodepisuje — jen ukazuje, co v datech smluv je.
 */

const LOCALE = 'cs' as const;

/** Stavy, ve kterých smlouva na někoho čeká (počítadlo v menu i tabulka). */
const PENDING_STATUSES = new Set(['ready_to_send', 'awaiting_signature']);

/**
 * Krok APP3 bod 1: prevod stavu na popisek je JEDEN — tutez tabulku mel
 * i editor smluv (`CONTRACT_STATUS_LABEL_KEY` v `lib/contract-standing.ts`).
 */
const STATUS_KEY = CONTRACT_STATUS_LABEL_KEY;

/** Počet smluv čekajících na podpis — mockup ho nese jako odznak v menu. */
export async function pendingSignatureCount(): Promise<number> {
  const rows = await listContracts({});
  return rows.filter((row) => PENDING_STATUSES.has(row.status)).length;
}

function formatDate(iso: string): string {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleDateString('cs-CZ');
}

/** Jména stran z `field_values` — bez rodných čísel a dalších citlivých polí. */
function partyNames(contract: ContractDto): string {
  const names: string[] = [];
  for (const [key, entry] of Object.entries(contract.fieldValues)) {
    if (!/^(pronajimatel|najemce|prodavajici|kupujici)\[\]$/.test(key)) continue;
    const value = entry.value;
    if (!Array.isArray(value)) continue;
    for (const item of value) {
      const name = (item as { jmeno?: unknown } | null)?.jmeno;
      if (typeof name === 'string' && name.trim() !== '') names.push(name);
    }
  }
  return names.join(' · ');
}

export function ElektronickePodpisyPage(): React.JSX.Element {
  const [rows, setRows] = React.useState<ContractDto[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    listContracts({}).then(
      (data) => {
        if (!cancelled) setRows(data.filter((row) => PENDING_STATUSES.has(row.status)));
      },
      () => {
        if (!cancelled) setError(t('webAdmin.contracts.loadError', { locale: LOCALE }));
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="p-6" data-testid="signatures-page">
      <h1 className="mb-1 text-xl font-bold">
        {t('webAdmin.signatures.title', { locale: LOCALE })}
      </h1>
      <p className="mb-4 max-w-3xl text-sm text-gray-600">
        {t('webAdmin.signatures.perex', { locale: LOCALE })}
      </p>

      {error !== null && <p role="alert">{error}</p>}
      {rows !== null && rows.length === 0 && error === null && (
        <EmptyState title={t('webAdmin.signatures.empty', { locale: LOCALE })} />
      )}
      {rows !== null && rows.length > 0 && (
        <>
          <p className="mb-2" data-testid="signatures-pending-count">
            {t('webAdmin.signatures.pendingCount', {
              locale: LOCALE,
              values: { count: rows.length },
            })}
          </p>
          <ListTable
            testId="signatures-table"
            caption={t('webAdmin.signatures.title', { locale: LOCALE })}
            rows={rows}
            rowKey={(row) => row.id}
            rowTestId={(row) => `signatures-row-${row.id}`}
            columns={[
              {
                key: 'contract',
                label: t('webAdmin.signatures.colContract', { locale: LOCALE }),
                cell: (row) =>
                  row.caseId === null ? (
                    row.title
                  ) : (
                    <Link
                      to="/zakazky/$id"
                      params={{ id: row.caseId }}
                      search={{ sekce: 'smlouva' }}
                      data-testid={`signatures-open-${row.id}`}
                    >
                      {row.title}
                    </Link>
                  ),
              },
              {
                key: 'parties',
                label: t('webAdmin.signatures.colParties', { locale: LOCALE }),
                cell: (row) => partyNames(row),
              },
              {
                key: 'status',
                label: t('webAdmin.signatures.colStatus', { locale: LOCALE }),
                cell: (row) => (
                  <span className="rh-badge">{t(STATUS_KEY[row.status], { locale: LOCALE })}</span>
                ),
              },
              {
                key: 'updated',
                label: t('webAdmin.signatures.colUpdated', { locale: LOCALE }),
                cell: (row) => formatDate(row.updatedAt),
              },
            ]}
          />
        </>
      )}
      <p className="mt-3 text-sm text-gray-600" data-testid="signatures-level-hint">
        {t('webAdmin.signatures.levelHint', { locale: LOCALE })}
      </p>
    </div>
  );
}
